import {
  Role,
  SubscriptionRenewalBanner,
  rolesManipulator,
  rolesMapping,
  useGetAppSelectorInfo,
  useGetUserInfo,
  useGetUserRoleRegistersIDs,
} from 'auth';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useSidebarProfileMenuOptions } from '@/modules/common/hooks/useSidebarProfileMenuOptions.ts';
import { queryClient } from '@/modules/common/query.ts';
import { AdminRegisterModal } from '@/modules/admin-dashboard/components/modals/AdminRegisterModal.tsx';
import { useCurrentUserRegistersQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { hasIncompleteRegistrationFields } from '@/modules/issuer-dashboard/helpers/registrationFields.ts';
import { useIssuerSidebarHeaderMenuOptions } from '@/modules/issuer-dashboard/hooks/useIssuerSidebarHeaderMenuOptions.tsx';
import { useIssuerSidebarOptions } from '@/modules/issuer-dashboard/hooks/useIssuerSidebarOptions.tsx';
import IssuerAbsentRegisters from '@/modules/issuer-dashboard/pages/IssuerAbsentRegisters.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { Banner, ModalProviderContext, Sidebar, Spinner, TopBar } from 'ui';
import { LogoSrWhite } from 'ui/icons/logo';

export interface IssuerDashboardWrapperOutletContext {
  registerId: string;
  registerName: string;
  allowsFractionalShares: boolean;
  registerCountry: string;
  registerCurrency: string;
}

const FINISH_REGISTRATION_BANNER_STORAGE_KEY = 'vestra_finish_registration_banner_dismissed';

type DismissedBannerMap = Record<string, string>;

const getCurrentDayKey = () => new Date().toISOString().slice(0, 10);

const IssuerDashboardWrapper = () => {
  const [params, setParams] = useSearchParams();
  const [registerId, setRegisterId] = useState(params.get(REGISTER_ID_SEARCH_PARAM_KEY));
  const [registerName, setRegisterName] = useState('');
  const [registerCountry, setRegisterCountry] = useState('');
  const [registerCurrency, setRegisterCurrency] = useState('');
  const [allowsFractionalShares, setAllowsFractionalShares] = useState(true);
  const [isBannerStorageReady, setIsBannerStorageReady] = useState(false);
  const [dismissedBannerByRegister, setDismissedBannerByRegister] = useState<DismissedBannerMap>(
    {},
  );
  const { avatar, first_name, last_name, email } = useGetUserInfo();
  const { openModal } = useContext(ModalProviderContext);
  const currentRoleRegistersIDs = useGetUserRoleRegistersIDs(rolesManipulator.current_user_role);

  const { data: registersResponse, isLoading: isRegistersLoading } =
    useCurrentUserRegistersQuery(true);

  const menuOptions = useIssuerSidebarOptions(registerId);
  const profileOptions = useSidebarProfileMenuOptions();
  const headerOptions = useIssuerSidebarHeaderMenuOptions(registersResponse);
  const appSelectorInfo = useGetAppSelectorInfo({
    onClickBeforeChangeRole: () => {
      queryClient.clear();
    },
  });
  const currentRegister = useMemo(
    () =>
      registerId
        ? registersResponse?.data.find(item => item.register_id === +registerId)?.register_obj
        : undefined,
    [registerId, registersResponse?.data],
  );
  const shouldShowFinishRegistrationBanner = useMemo(() => {
    if (
      !isBannerStorageReady ||
      !registerId ||
      !currentRegister ||
      !hasIncompleteRegistrationFields(currentRegister)
    ) {
      return false;
    }

    return dismissedBannerByRegister[registerId] !== getCurrentDayKey();
  }, [currentRegister, dismissedBannerByRegister, isBannerStorageReady, registerId]);

  useEffect(() => {
    const storedValue = localStorage.getItem(FINISH_REGISTRATION_BANNER_STORAGE_KEY);

    if (!storedValue) {
      setIsBannerStorageReady(true);
      return;
    }

    try {
      const parsedValue = JSON.parse(storedValue);
      setDismissedBannerByRegister(
        parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
          ? parsedValue
          : {},
      );
    } catch {
      setDismissedBannerByRegister({});
    } finally {
      setIsBannerStorageReady(true);
    }
  }, []);

  const handleOpenFinishRegistration = useCallback(() => {
    if (!registerId) return;

    openModal(AdminRegisterModal, {
      redactionRegisterId: +registerId,
      role: 'issuer',
      isFinishRegistrationMode: true,
    });
  }, [openModal, registerId]);

  const handleDismissFinishRegistrationBanner = useCallback(() => {
    if (!registerId) return;

    setDismissedBannerByRegister(prev => {
      const nextValue = { ...prev, [registerId]: getCurrentDayKey() };
      localStorage.setItem(FINISH_REGISTRATION_BANNER_STORAGE_KEY, JSON.stringify(nextValue));
      return nextValue;
    });
  }, [registerId]);

  useEffect(() => {
    const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

    if (register_id && register_id !== registerId && !isNaN(+register_id)) {
      setRegisterId(register_id);
      if (registersResponse?.data) {
        const registerObj = registersResponse?.data.find(
          item => item.register_id === +register_id,
        )?.register_obj;
        const name = registerObj?.name ?? '';
        const country = registerObj?.country_code ?? '';
        const currency = registerObj?.currency?.toUpperCase() ?? '';
        setRegisterName(name);
        setRegisterCountry(country);
        setRegisterCurrency(currency);
      }
    }
  }, [params, registerId, registersResponse?.data]);

  useEffect(() => {
    const allRegisters = registersResponse?.data?.map(item => item.register_id);
    if (registerId && registersResponse?.data) {
      const currentRegister = registersResponse?.data?.find(
        item => item.register_id === +registerId,
      );
      if (currentRegister) {
        setAllowsFractionalShares(currentRegister?.register_obj?.allows_fractional_shares === true);
        setRegisterName(currentRegister?.register_obj?.name ?? '');
        setRegisterCountry(currentRegister?.register_obj?.country_code ?? '');
        setRegisterCurrency(currentRegister?.register_obj?.currency?.toUpperCase() ?? '');
      }
    }

    if (
      allRegisters?.length &&
      currentRoleRegistersIDs.length &&
      (!registerId || !allRegisters.includes(+registerId))
    ) {
      const newRegisterId = currentRoleRegistersIDs[0];
      params.set(REGISTER_ID_SEARCH_PARAM_KEY, newRegisterId.toString());
      setParams(params, { replace: false });

      const newRegister = registersResponse?.data.find(
        ({ register_id }) => register_id === newRegisterId,
      )?.register_obj;

      if (newRegister) {
        setAllowsFractionalShares(!!newRegister.allows_fractional_shares);
        setRegisterName(newRegister.name);
        setRegisterCountry(newRegister.country_code ?? '');
        setRegisterCurrency(newRegister.currency?.toUpperCase() ?? '');
      }
    }
  }, [currentRoleRegistersIDs, params, registerId, registersResponse, setParams]);

  return (
    <div
      className={classNames('bg-gray-900 sm:h-screen w-full flex sm:flex-row flex-col h-[100dvh]')}
    >
      <TopBar
        label={
          <Link to={{ pathname: issuerDashboardPaths.static.dashboard, search: params.toString() }}>
            <img src={LogoSrWhite} alt="logo" className="h-8" />
          </Link>
        }
        avatarUrl={avatar}
        appSelectorInfo={appSelectorInfo}
        menuItems={menuOptions}
        profileMenuItems={profileOptions}
        headerMenuItems={headerOptions}
        headerMenuSelectedValue={params.get(REGISTER_ID_SEARCH_PARAM_KEY) || ''}
        header="menu"
        user={{ email, first_name, last_name }}
        entity={{ type: 'Issuer' }}
        role={
          rolesManipulator?.current_user_role
            ? rolesMapping[rolesManipulator?.current_user_role].name
            : ''
        }
      />
      <Sidebar
        label={
          <Link to={{ pathname: issuerDashboardPaths.static.dashboard, search: params.toString() }}>
            <img src={LogoSrWhite} alt="logo" className="h-8" />
          </Link>
        }
        avatarUrl={avatar}
        appSelectorInfo={appSelectorInfo}
        menuItems={menuOptions}
        profileMenuItems={profileOptions}
        headerMenuItems={headerOptions}
        headerMenuSelectedItem={headerOptions.find(
          ({ value }) => value === params.get(REGISTER_ID_SEARCH_PARAM_KEY),
        )}
        isHeaderMenuItemsLoading={isRegistersLoading}
        user={{ email, first_name, last_name }}
        entity={{ type: 'Issuer' }}
        role={
          rolesManipulator?.current_user_role
            ? rolesMapping[rolesManipulator?.current_user_role].name
            : ''
        }
      />

      <main className="flex w-full flex-col sm:h-full h-[calc(100%-56px)] overflow-hidden">
        <SubscriptionRenewalBanner
          identityCenterApiUrl={import.meta.env.VITE_IDENTITY_CENTER_API_URL}
          selectedApp="sr"
          storageScope="sr-issuer"
        />
        {shouldShowFinishRegistrationBanner && (
          <Banner
            onClose={handleDismissFinishRegistrationBanner}
            closeLabel="Close registration banner"
          >
            <>
              Your registration is not finished yet. Some features may not work properly until you
              complete your organisation details. You can finish it here:{' '}
              <button
                type="button"
                className="inline font-semibold underline underline-offset-2 hover:opacity-70"
                onClick={handleOpenFinishRegistration}
              >
                open the finish registration panel
              </button>
              .
            </>
          </Banner>
        )}
        <div
          className={classNames(
            'overflow-y-auto overflow-x-hidden flex flex-col w-full sm:h-full h-full min-w-0',
            'sm:flex-grow-0 flex-grow relative top-0 bottom-0',
            'bg-white justify-center sm:rounded-tr-none rounded-bl-none',
            {
              'items-center': isRegistersLoading || !registersResponse || !registerId,
              'items-start': !(isRegistersLoading || !registersResponse || !registerId),
              'rounded-tl-xl rounded-tr-xl sm:rounded-bl-xl': !shouldShowFinishRegistrationBanner,
              'rounded-tl-none rounded-tr-none sm:rounded-bl-xl':
                shouldShowFinishRegistrationBanner,
            },
          )}
        >
          {isRegistersLoading && <Spinner size="lg" />}
          {!isRegistersLoading && registersResponse && !registersResponse.data.length && (
            <IssuerAbsentRegisters />
          )}
          {!isRegistersLoading &&
            registersResponse &&
            registerId &&
            !!registersResponse.data.length && (
              <Outlet
                context={
                  {
                    registerId,
                    allowsFractionalShares: allowsFractionalShares ?? false,
                    registerName: registerName ?? '',
                    registerCountry: registerCountry ?? '',
                    registerCurrency: registerCurrency ?? '',
                  } as IssuerDashboardWrapperOutletContext
                }
              />
            )}
        </div>
      </main>
    </div>
  );
};

export default withPrivateAccess(IssuerDashboardWrapper, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
